import { useRef } from 'react';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useTranslation } from 'react-i18next';

export default function SectionDivider({ variant = 'glow', className = '', fromColor, toColor }) {
  if (variant === 'brand') {
    return <BrandDivider fromColor={fromColor} toColor={toColor} className={className} />;
  }
  if (variant === 'fade') {
    return (
      <div className={`relative h-24 md:h-32 ${className}`} style={{ background: 'transparent' }} aria-hidden="true" />
    );
  }
  if (variant === 'mesh') {
    return (
      <div className={`relative h-32 md:h-40 overflow-hidden ${className}`} aria-hidden="true">
        <div className="absolute left-1/4 top-1/2 -translate-y-1/2 w-80 h-20 rounded-full blur-[80px] opacity-[0.07]" style={{ background: 'linear-gradient(90deg, #A84397, #D23CB3)' }} />
        <div className="absolute right-1/4 top-1/2 -translate-y-1/2 w-60 h-16 rounded-full blur-[60px] opacity-[0.05]" style={{ background: '#A84397' }} />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2" style={{ width: '40%', maxWidth: '400px', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(168,67,151,0.3), rgba(210,60,179,0.3), rgba(168,67,151,0.3), transparent)' }} />
      </div>
    );
  }
  return (
    <div className={`relative py-8 md:py-12 ${className}`} aria-hidden="true">
      <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-96 h-8 rounded-full blur-[40px] opacity-[0.12]" style={{ background: 'linear-gradient(90deg, #A84397, #D23CB3)' }} />
      <div className="mx-auto" style={{ maxWidth: '600px', width: '60%', height: '1px', background: 'linear-gradient(90deg, transparent, rgba(168,67,151,0.4), rgba(210,60,179,0.5), rgba(168,67,151,0.4), transparent)' }} />
    </div>
  );
}

/* ── Neural network nodes — more complex ── */
const coreNodes = Array.from({ length: 4 }, (_, i) => {
  const angle = (i / 4) * Math.PI * 2 - Math.PI / 4;
  return { x: Math.cos(angle) * 40, y: Math.sin(angle) * 40, delay: i * 0.08 };
});
const innerNodes = Array.from({ length: 8 }, (_, i) => {
  const angle = (i / 8) * Math.PI * 2 - Math.PI / 2;
  return { x: Math.cos(angle) * 90, y: Math.sin(angle) * 90, delay: i * 0.08 };
});
const middleNodes = Array.from({ length: 14 }, (_, i) => {
  const angle = (i / 14) * Math.PI * 2 - Math.PI / 6;
  return { x: Math.cos(angle) * 155, y: Math.sin(angle) * 155, delay: i * 0.06 };
});
const outerNodes = Array.from({ length: 20 }, (_, i) => {
  const angle = (i / 20) * Math.PI * 2;
  return { x: Math.cos(angle) * 220, y: Math.sin(angle) * 220, delay: i * 0.04 };
});

/* ── Stars for galaxy background ── */
const stars = Array.from({ length: 80 }, (_, i) => ({
  x: Math.random() * 100,
  y: Math.random() * 100,
  size: 0.5 + Math.random() * 1.5,
  opacity: 0.03 + Math.random() * 0.08,
  duration: 3 + Math.random() * 5,
  delay: Math.random() * 4,
}));

/* ── Service icons orbiting ── */
const orbitIcons = [
  { icon: 'shield', label: 'Seguridad', angle: 0 },
  { icon: 'public', label: 'Global', angle: 60 },
  { icon: 'code', label: 'API', angle: 120 },
  { icon: 'psychology', label: 'IA', angle: 180 },
  { icon: 'account_balance', label: 'Banca', angle: 240 },
  { icon: 'speed', label: 'Uptime', angle: 300 },
];

/* ── Neural connections — denser network ── */
function getConnections() {
  const conns = [];
  // Center to core
  coreNodes.forEach((n, i) => {
    conns.push({ x1: 0, y1: 0, x2: n.x, y2: n.y, delay: i * 0.08 });
  });
  // Core to inner
  coreNodes.forEach((n, i) => {
    const i1 = innerNodes[i * 2 % innerNodes.length];
    const i2 = innerNodes[(i * 2 + 1) % innerNodes.length];
    conns.push({ x1: n.x, y1: n.y, x2: i1.x, y2: i1.y, delay: 0.2 + i * 0.1 });
    conns.push({ x1: n.x, y1: n.y, x2: i2.x, y2: i2.y, delay: 0.3 + i * 0.1 });
  });
  // Inner to middle
  innerNodes.forEach((n, i) => {
    const m1 = middleNodes[i % middleNodes.length];
    const m2 = middleNodes[(i + 1) % middleNodes.length];
    const m3 = middleNodes[(i + 2) % middleNodes.length];
    conns.push({ x1: n.x, y1: n.y, x2: m1.x, y2: m1.y, delay: 0.4 + i * 0.08 });
    conns.push({ x1: n.x, y1: n.y, x2: m2.x, y2: m2.y, delay: 0.5 + i * 0.08 });
    if (i % 2 === 0) conns.push({ x1: n.x, y1: n.y, x2: m3.x, y2: m3.y, delay: 0.55 + i * 0.06 });
  });
  // Inner cross-connections
  innerNodes.forEach((n, i) => {
    if (i % 2 === 0) {
      const next = innerNodes[(i + 2) % innerNodes.length];
      conns.push({ x1: n.x, y1: n.y, x2: next.x, y2: next.y, delay: 0.6 + i * 0.05 });
    }
  });
  // Middle to outer
  middleNodes.forEach((n, i) => {
    const o1 = outerNodes[Math.floor(i * 1.4) % outerNodes.length];
    const o2 = outerNodes[Math.floor(i * 1.4 + 1) % outerNodes.length];
    conns.push({ x1: n.x, y1: n.y, x2: o1.x, y2: o1.y, delay: 0.7 + i * 0.06 });
    if (i % 2 === 0) conns.push({ x1: n.x, y1: n.y, x2: o2.x, y2: o2.y, delay: 0.8 + i * 0.05 });
  });
  // Middle cross-connections
  middleNodes.forEach((n, i) => {
    if (i % 3 === 0) {
      const next = middleNodes[(i + 3) % middleNodes.length];
      conns.push({ x1: n.x, y1: n.y, x2: next.x, y2: next.y, delay: 0.9 + i * 0.04 });
    }
  });
  return conns;
}
const connections = getConnections();

function BrandDivider({ fromColor, toColor, className }) {
  const { t } = useTranslation();
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start end', 'end start'],
  });

  const containerOpacity = useTransform(scrollYProgress, [0.05, 0.25], [0, 1]);
  const containerScale = useTransform(scrollYProgress, [0.05, 0.35], [0.85, 1]);
  const logoOpacity = useTransform(scrollYProgress, [0.15, 0.35], [0, 1]);
  const logoScale = useTransform(scrollYProgress, [0.15, 0.3, 0.4], [0.3, 1.1, 1]);
  const glowOpacity = useTransform(scrollYProgress, [0.2, 0.5], [0, 0.25]);
  const textOpacity = useTransform(scrollYProgress, [0.4, 0.55], [0, 1]);
  const textY = useTransform(scrollYProgress, [0.4, 0.55], [20, 0]);
  const orbitOpacity = useTransform(scrollYProgress, [0.3, 0.5], [0, 1]);

  const cx = 250;
  const cy = 250;

  return (
    <div
      ref={ref}
      className={`relative py-20 md:py-28 overflow-hidden ${className}`}
      style={{ background: 'transparent' }}
      aria-hidden="true"
    >
      {/* Stars / galaxy background */}
      {stars.map((star, i) => (
        <motion.div
          key={`star${i}`}
          className="absolute rounded-full bg-white pointer-events-none"
          style={{
            left: `${star.x}%`,
            top: `${star.y}%`,
            width: `${star.size}px`,
            height: `${star.size}px`,
          }}
          animate={{ opacity: [star.opacity, star.opacity * 2.5, star.opacity] }}
          transition={{ duration: star.duration, repeat: Infinity, ease: 'easeInOut', delay: star.delay }}
        />
      ))}

      {/* Ambient glow */}
      <motion.div
        className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full blur-[150px]"
        style={{ background: 'radial-gradient(circle, rgba(168,67,151,0.25), rgba(210,60,179,0.1), transparent)', opacity: glowOpacity }}
      />

      <div className="flex flex-col items-center justify-center">
        <motion.div
          className="relative mb-6 h-[350px] w-[350px] md:mb-10 md:h-[500px] md:w-[500px]"
          style={{ opacity: containerOpacity, scale: containerScale }}
        >
          <svg viewBox="0 0 500 500" className="w-full h-full" fill="none">
            <defs>
              <linearGradient id="nnLine" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop offset="0%" stopColor="#A84397" stopOpacity="0.4" />
                <stop offset="100%" stopColor="#D23CB3" stopOpacity="0.15" />
              </linearGradient>
              <radialGradient id="nnNodeGrad">
                <stop offset="0%" stopColor="#D23CB3" />
                <stop offset="100%" stopColor="#A84397" />
              </radialGradient>
              <filter id="nnGlow">
                <feGaussianBlur stdDeviation="3" />
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
              <filter id="nnBigGlow">
                <feGaussianBlur stdDeviation="8" />
                <feMerge><feMergeNode /><feMergeNode in="SourceGraphic" /></feMerge>
              </filter>
            </defs>

            {/* ── Orbit rings ── */}
            <circle cx={cx} cy={cy} r="40" stroke="rgba(210,60,179,0.1)" strokeWidth="0.5" fill="none" />
            <circle cx={cx} cy={cy} r="90" stroke="rgba(168,67,151,0.08)" strokeWidth="0.5" fill="none" />
            <circle cx={cx} cy={cy} r="155" stroke="rgba(168,67,151,0.06)" strokeWidth="0.5" fill="none" strokeDasharray="4 4" />
            <circle cx={cx} cy={cy} r="220" stroke="rgba(168,67,151,0.04)" strokeWidth="0.5" fill="none" strokeDasharray="2 6" />

            {/* ── Neural connections ── */}
            {connections.map((c, i) => (
              <motion.line
                key={i}
                x1={cx + c.x1} y1={cy + c.y1}
                x2={cx + c.x2} y2={cy + c.y2}
                stroke="url(#nnLine)"
                strokeWidth="0.8"
                initial={{ pathLength: 0, opacity: 0 }}
                whileInView={{ pathLength: 1, opacity: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: c.delay }}
              />
            ))}

            {/* ── Data pulses traveling along connections ── */}
            {connections.filter((_, i) => i % 3 === 0).map((c, i) => (
              <motion.circle
                key={`p${i}`}
                r="1.5"
                fill="#D23CB3"
                filter="url(#nnGlow)"
              >
                <animateMotion
                  dur={`${2 + (i % 2)}s`}
                  repeatCount="indefinite"
                  begin={`${c.delay}s`}
                  path={`M${cx + c.x1},${cy + c.y1} L${cx + c.x2},${cy + c.y2}`}
                />
              </motion.circle>
            ))}

            {/* ── Outer nodes ── */}
            {outerNodes.map((n, i) => (
              <motion.circle
                key={`o${i}`}
                cx={cx + n.x} cy={cy + n.y} r="2.5"
                fill="#A84397"
                filter="url(#nnGlow)"
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 0.6, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 1 + n.delay, duration: 0.4 }}
              />
            ))}

            {/* ── Middle nodes ── */}
            {middleNodes.map((n, i) => (
              <motion.circle
                key={`m${i}`}
                cx={cx + n.x} cy={cy + n.y} r="3.5"
                fill="url(#nnNodeGrad)"
                filter="url(#nnGlow)"
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 0.8, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.6 + n.delay, duration: 0.4 }}
              />
            ))}

            {/* ── Core nodes (closest to logo) ── */}
            {coreNodes.map((n, i) => (
              <motion.circle
                key={`core${i}`}
                cx={cx + n.x} cy={cy + n.y} r="4"
                fill="#D23CB3"
                filter="url(#nnGlow)"
                initial={{ opacity: 0, scale: 0 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: 0.15 + i * 0.08, duration: 0.4 }}
              />
            ))}

            {/* ── Inner nodes ── */}
            {innerNodes.map((n, i) => (
              <g key={`in${i}`}>
                <motion.circle
                  cx={cx + n.x} cy={cy + n.y} r="5"
                  fill="url(#nnNodeGrad)"
                  filter="url(#nnGlow)"
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 0.3 + n.delay, duration: 0.5 }}
                />
                <motion.circle
                  cx={cx + n.x} cy={cy + n.y} r="8"
                  fill="none" stroke="#D23CB3" strokeWidth="0.5"
                  animate={{ r: [7, 12, 7], opacity: [0.3, 0.05, 0.3] }}
                  transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut', delay: i * 0.4 }}
                />
              </g>
            ))}

            {/* ── Center glow ── */}
            <motion.circle
              cx={cx} cy={cy} r="45"
              fill="none"
              stroke="#D23CB3"
              strokeWidth="0.6"
              animate={{ r: [40, 50, 40], opacity: [0.2, 0.05, 0.2] }}
              transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
            />
            <motion.circle
              cx={cx} cy={cy} r="30"
              fill="radial-gradient(circle, rgba(210,60,179,0.15), transparent)"
              style={{ filter: 'blur(10px)' }}
              animate={{ r: [25, 35, 25], opacity: [0.3, 0.1, 0.3] }}
              transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            />
          </svg>

          {/* ── CrossPay logo at exact center ── */}
          <motion.div
            className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none"
            style={{ opacity: logoOpacity, scale: logoScale }}
          >
            <div className="relative">
              <div className="absolute -inset-8 rounded-full blur-[30px] opacity-50" style={{ background: 'radial-gradient(circle, rgba(210,60,179,0.5), rgba(168,67,151,0.2), transparent)' }} />
              <motion.img
                src="/logo-icon.png"
                alt=""
                className="w-16 h-16 md:w-20 md:h-20 object-contain relative z-10 select-none"
                style={{ filter: 'drop-shadow(0 0 15px rgba(210,60,179,0.5)) drop-shadow(0 0 30px rgba(168,67,151,0.3))' }}
                animate={{ scale: [1, 1.05, 1] }}
                transition={{ duration: 4, repeat: Infinity, ease: 'easeInOut' }}
              />
            </div>
          </motion.div>

          {/* ── Orbiting service icons ── */}
          <motion.div
            className="absolute inset-0"
            style={{ opacity: orbitOpacity }}
          >
            {orbitIcons.map((item, i) => {
              const mdR = 240;
              const rad = (item.angle * Math.PI) / 180;
              const mdX = 50 + (Math.cos(rad) * mdR / 500) * 100;
              const mdY = 50 + (Math.sin(rad) * mdR / 500) * 100;
              return (
                <motion.div
                  key={item.icon}
                  className="absolute flex flex-col items-center gap-1"
                  style={{ left: `${mdX}%`, top: `${mdY}%`, transform: 'translate(-50%, -50%)' }}
                  initial={{ opacity: 0, scale: 0 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: 1 + i * 0.15, duration: 0.5, type: 'spring' }}
                >
                  <motion.div
                    className="w-10 h-10 md:w-11 md:h-11 rounded-xl bg-purple-primary/10 border border-purple-primary/25 flex items-center justify-center backdrop-blur-sm"
                    whileHover={{ scale: 1.15, borderColor: 'rgba(210,60,179,0.5)' }}
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 3 + i * 0.5, repeat: Infinity, ease: 'easeInOut' }}
                  >
                    <span className="material-symbols-outlined text-purple-primary text-[20px]">{item.icon}</span>
                  </motion.div>
                  <span className="text-[8px] md:text-[9px] text-on-surface-variant/50 font-label uppercase tracking-wider">{item.label}</span>
                </motion.div>
              );
            })}
          </motion.div>
        </motion.div>

        {/* ── Text (más separación respecto al gráfico animado) ── */}
        <motion.div className="mt-12 text-center md:mt-16" style={{ opacity: textOpacity, y: textY }}>
          <p className="text-on-surface-variant/70 text-sm md:text-base font-label uppercase tracking-[5px]">
            {t('brandDivider.line1')}
          </p>
          <p className="gradient-text text-2xl md:text-4xl font-black font-headline mt-2">
            {t('brandDivider.line2')}
          </p>
        </motion.div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-20 pointer-events-none" style={{ background: 'transparent' }} />
    </div>
  );
}
